import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, List, message, Row, Col, Spin } from 'antd';
import { Link } from 'react-router-dom';
import profileService from '../../../services/profileService';
import formService from '../../../services/formService';

const text = {
  listTitle: '\u6559\u5e08\u7ba1\u7406',
  advisorTitle: '\u8bfe\u7a0b\u987e\u95ee\u7ba1\u7406',
  teacherDetailTitle: '\u6559\u5e08 Profile',
  advisorDetailTitle: '\u8bfe\u7a0b\u987e\u95ee Profile',
  username: '\u7528\u6237\u540d',
  realName: '\u59d3\u540d',
  email: '\u90ae\u7bb1',
  phone: '\u7535\u8bdd',
  address: '\u5730\u5740',
  abn: 'ABN',
  salary: '\u5de5\u8d44',
  hourlyFee: '\u8bfe\u65f6\u8d39',
  courseCommission: '\u4e1a\u7ee9\u63d0\u6210',
  referralCommission: '\u63a8\u8350\u63d0\u6210',
  salarySaved: '\u5de5\u8d44\u5df2\u4fdd\u5b58',
  changePassword: '\u66f4\u6539\u5bc6\u7801',
  newPassword: '\u65b0\u5bc6\u7801',
  edit: '\u66f4\u6539',
  save: '\u4fdd\u5b58',
  cancel: '\u53d6\u6d88',
  back: '\u8fd4\u56de\u7ba1\u7406\u5217\u8868',
  backProfile: '\u8fd4\u56de Profile',
  loadFailed: '\u8bfb\u53d6\u6559\u5e08\u4fe1\u606f\u5931\u8d25',
  saveFailed: '\u4fdd\u5b58\u5931\u8d25',
  saveSuccess: '\u6559\u5e08\u4fe1\u606f\u5df2\u66f4\u65b0',
  active: '\u5728\u804c',
  inactive: '\u79bb\u804c',
  statusFailed: '\u72b6\u6001\u4fdd\u5b58\u5931\u8d25',
  emptyTeacher: '\u6682\u65e0\u6559\u5e08',
  emptyAdvisor: '\u6682\u65e0\u8bfe\u7a0b\u987e\u95ee',
};

const sortUsers = (list) =>
  [...list].sort((a, b) => {
    if (a.isActive === b.isActive) {
      return (a.cnname || a.name || '').localeCompare(b.cnname || b.name || '');
    }
    return a.isActive === false ? 1 : -1;
  });

const StaffCard = ({
  title,
  users,
  loading,
  savingId,
  emptyText,
  onStatusChange,
}) => (
  <Card title={title} style={{ flex: 1, minWidth: 360 }}>
    <Spin spinning={loading}>
      <List
        dataSource={users}
        locale={{ emptyText }}
        renderItem={(user) => (
          <List.Item
            actions={[
              user.isActive === false ? (
                <Button
                  size='small'
                  loading={savingId === user._id}
                  onClick={() => onStatusChange(user, true)}
                >
                  {text.inactive}
                </Button>
              ) : (
                <Button
                  size='small'
                  type='primary'
                  loading={savingId === user._id}
                  onClick={() => onStatusChange(user, false)}
                >
                  {text.active}
                </Button>
              ),
            ]}
          >
            <Link
              to={`/teacher-management/${user._id}`}
              style={{
                color: user.isActive === false ? '#999' : undefined,
                textDecoration: user.isActive === false ? 'line-through' : 'none',
              }}
            >
              {user.cnname || user.name}
            </Link>
          </List.Item>
        )}
      />
    </Spin>
  </Card>
);

const TeacherList = () => {
  const [teacherLoading, setTeacherLoading] = useState(true);
  const [advisorLoading, setAdvisorLoading] = useState(true);
  const [savingId, setSavingId] = useState('');
  const [teachers, setTeachers] = useState([]);
  const [advisors, setAdvisors] = useState([]);

  useEffect(() => {
    profileService
      .getTeachers(true)
      .then((res) => {
        setTeachers(sortUsers((res.data || []).filter((item) => item.role === 2)));
      })
      .catch(() => message.error(text.loadFailed))
      .finally(() => setTeacherLoading(false));

    profileService
      .getAdvisors(true)
      .then((res) => {
        setAdvisors(sortUsers(res.data || []));
      })
      .catch(() => message.error(text.loadFailed))
      .finally(() => setAdvisorLoading(false));
  }, []);

  const handleStatusChange = async (user, isActive) => {
    setSavingId(user._id);
    try {
      await profileService.updateUser(user._id, { isActive, role: user.role });
      localStorage.removeItem('TEACHER_LIST');
      localStorage.removeItem('ADVISOR_LIST');
      if (user.role === 2) {
        setTeachers((prev) =>
          sortUsers(
            prev.map((item) =>
              item._id === user._id ? { ...item, isActive } : item
            )
          )
        );
      } else {
        setAdvisors((prev) =>
          sortUsers(
            prev.map((item) =>
              item._id === user._id ? { ...item, isActive } : item
            )
          )
        );
      }
    } catch (err) {
      message.error(text.statusFailed);
    } finally {
      setSavingId('');
    }
  };

  return (
    <div
      style={{
        maxWidth: 1120,
        margin: '40px auto',
        display: 'flex',
        gap: 24,
        alignItems: 'flex-start',
      }}
    >
      <StaffCard
        title={text.listTitle}
        users={teachers}
        loading={teacherLoading}
        savingId={savingId}
        emptyText={text.emptyTeacher}
        onStatusChange={handleStatusChange}
      />
      <StaffCard
        title={text.advisorTitle}
        users={advisors}
        loading={advisorLoading}
        savingId={savingId}
        emptyText={text.emptyAdvisor}
        onStatusChange={handleStatusChange}
      />
    </div>
  );
};

const TeacherProfile = ({ teacherId }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [teacherData, setTeacherData] = useState({});

  useEffect(() => {
    profileService
      .getUser(teacherId)
      .then((res) => {
        const {
          name,
          cnname,
          email,
          phone,
          address,
          abn,
          role,
        } = res.data;
        const values = {
          name,
          cnname,
          email,
          phone,
          address,
          abn,
          role,
        };
        setTeacherData(values);
        form.setFieldsValue(values);
      })
      .catch(() => message.error(text.loadFailed))
      .finally(() => setLoading(false));
  }, [form, teacherId]);

  const handleSave = async (values) => {
    if (!editing) {
      return;
    }
    const payload = {
      ...values,
      role: teacherData.role || 2,
    };
    if (!payload.password) {
      delete payload.password;
    }
    setSaving(true);
    try {
      await profileService.updateUser(teacherId, payload);
      message.success(text.saveSuccess);
      setTeacherData({ ...payload, password: undefined });
      form.setFieldsValue({ password: '' });
      setEditing(false);
    } catch (err) {
      message.error(text.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
      <Card
        title={
          Number(teacherData.role) === 2
            ? text.teacherDetailTitle
            : text.advisorDetailTitle
        }
        style={{ width: 460 }}
        extra={
          <>
            <Link to={`/teacher-management/${teacherId}/salary`}>
              <Button type='primary' size='small' style={{ marginRight: 16 }}>
                {text.salary}
              </Button>
            </Link>
            <Link to='/teacher-management'>{text.back}</Link>
          </>
        }
      >
        <Spin spinning={loading}>
          <Form
            layout='vertical'
            form={form}
            onFinish={handleSave}
            onSubmitCapture={(event) => {
              if (!editing) {
                event.preventDefault();
                event.stopPropagation();
              }
            }}
          >
            <Row>
              <Col span={24}>
                <Form.Item
                  label={text.username}
                  name='name'
                  rules={[{ required: true, message: '\u8bf7\u8f93\u5165\u7528\u6237\u540d' }]}
                >
                  <Input disabled={!editing} />
                </Form.Item>
                <Form.Item
                  label={text.realName}
                  name='cnname'
                  rules={[{ required: true, message: '\u8bf7\u8f93\u5165\u59d3\u540d' }]}
                >
                  <Input disabled={!editing} />
                </Form.Item>
                <Form.Item label={text.email} name='email'>
                  <Input disabled={!editing} />
                </Form.Item>
                <Form.Item label={text.phone} name='phone'>
                  <Input disabled={!editing} />
                </Form.Item>
                <Form.Item label={text.address} name='address'>
                  <Input disabled={!editing} />
                </Form.Item>
                <Form.Item label={text.abn} name='abn'>
                  <Input disabled={!editing} />
                </Form.Item>
              </Col>
            </Row>
            {editing && (
              <>
                <div style={{ fontWeight: 500, margin: '20px 0 8px' }}>
                  {text.changePassword}
                </div>
                <Form.Item label={text.newPassword} name='password'>
                  <Input.Password placeholder='留空表示不修改密码' />
                </Form.Item>
              </>
            )}
          </Form>
          <div style={{ marginTop: 24 }}>
            {editing ? (
              <>
                <Button
                  type='primary'
                  htmlType='button'
                  loading={saving}
                  onClick={() => form.submit()}
                >
                  {text.save}
                </Button>
                <Button
                  htmlType='button'
                  style={{ marginLeft: 12 }}
                  onClick={() => {
                    form.setFieldsValue({ ...teacherData, password: '' });
                    setEditing(false);
                  }}
                >
                  {text.cancel}
                </Button>
              </>
            ) : (
              <Button
                type='primary'
                htmlType='button'
                onClick={() => setEditing(true)}
              >
                {text.edit}
              </Button>
            )}
          </div>
        </Spin>
      </Card>
    </div>
  );
};

const TeacherSalary = ({ teacherId, history }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [teacherData, setTeacherData] = useState({});
  const [courseTypes, setCourseTypes] = useState([]);

  useEffect(() => {
    Promise.all([
      profileService.getUser(teacherId),
      formService.queryCourseType(),
    ])
      .then(([userRes, courseTypeRes]) => {
        const user = userRes.data || {};
        setTeacherData(user);
        setCourseTypes(courseTypeRes.data || []);
        form.setFieldsValue({
          courseCommissions: {
            referralCommission: 10,
            ...(user.courseCommissions || {}),
          },
        });
      })
      .catch(() => message.error(text.loadFailed))
      .finally(() => setLoading(false));
  }, [form, teacherId]);

  const handleSave = async (values, returnProfile = false) => {
    const courseCommissions = {};
    Object.keys(values.courseCommissions || {}).forEach((key) => {
      const value = values.courseCommissions[key];
      if (value !== undefined && value !== null && value !== '') {
        courseCommissions[key] = Number(value);
      }
    });

    setSaving(true);
    try {
      await profileService.updateUser(teacherId, {
        role: teacherData.role || 2,
        courseCommissions,
      });
      setTeacherData({
        ...teacherData,
        courseCommissions,
      });
      form.setFieldsValue({ courseCommissions });
      setEditing(false);
      message.success(text.salarySaved);
      if (returnProfile) {
        history.push(`/teacher-management/${teacherId}`);
      }
    } catch (err) {
      message.error(text.saveFailed);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 40 }}>
      <Card
        title={text.salary}
        style={{ width: 460 }}
        extra={(
          <Link
            to={`/teacher-management/${teacherId}`}
            onClick={(event) => {
              if (!editing) {
                return;
              }
              event.preventDefault();
              form.validateFields().then((values) => handleSave(values, true));
            }}
          >
            {text.backProfile}
          </Link>
        )}
      >
        <Spin spinning={loading}>
          <Form
            layout='horizontal'
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            form={form}
            onFinish={handleSave}
            labelAlign='left'
          >
            <div style={{ fontWeight: 600, marginBottom: 16 }}>{text.hourlyFee}</div>
            {courseTypes.map((courseType) => (
              <Form.Item
                key={courseType.type}
                label={courseType.name}
                name={['courseCommissions', courseType.type]}
              >
                <InputNumber
                  disabled={!editing}
                  min={0}
                  precision={2}
                  style={{ width: 160 }}
                />
              </Form.Item>
            ))}
            <Form.Item
              label={text.courseCommission}
              name={['courseCommissions', 'courseCommission']}
              style={{ marginTop: 24 }}
            >
              <InputNumber
                disabled={!editing}
                min={0}
                precision={2}
                formatter={(value) => (value || value === 0 ? `${value}%` : '')}
                parser={(value) => value.replace('%', '')}
                style={{ width: 160 }}
              />
            </Form.Item>
            <Form.Item
              label={text.referralCommission}
              name={['courseCommissions', 'referralCommission']}
            >
              <InputNumber
                disabled={!editing}
                min={0}
                precision={2}
                formatter={(value) => (value || value === 0 ? `${value}%` : '')}
                parser={(value) => value.replace('%', '')}
                style={{ width: 160 }}
              />
            </Form.Item>
            {editing ? (
              <div>
                <Button type='primary' htmlType='submit' loading={saving}>
                  {text.save}
                </Button>
                <Button
                  htmlType='button'
                  style={{ marginLeft: 12 }}
                  onClick={() => {
                    form.setFieldsValue({
                      courseCommissions: teacherData.courseCommissions || {},
                    });
                    setEditing(false);
                  }}
                >
                  {text.cancel}
                </Button>
              </div>
            ) : (
              <Button
                type='primary'
                htmlType='button'
                onClick={() => setEditing(true)}
              >
                {text.edit}
              </Button>
            )}
          </Form>
        </Spin>
      </Card>
    </div>
  );
};

const TeacherManagement = ({ match, history }) => {
  const teacherId = match.params.id;
  if (teacherId && match.url.endsWith('/salary')) {
    return <TeacherSalary teacherId={teacherId} history={history} />;
  }
  return teacherId ? <TeacherProfile teacherId={teacherId} /> : <TeacherList />;
};

export default TeacherManagement;
